import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { AsyncPipe } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatNavList } from "@angular/material/list";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatTooltipModule } from "@angular/material/tooltip";
import { Title } from "@angular/platform-browser";
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from "@angular/router";
import { defaultProfilePic } from "@config/asset.config";
import { IApiResponse } from "@models/api-response.model";
import { ChatService } from "@services/chat/chat.service";
import { AppStateService } from "@services/index";
import { RequestService } from "@services/request/request.service";
import { Observable, Subscription } from "rxjs";
import { filter, map, shareReplay } from "rxjs/operators";
import { ImageUrlDirective } from "../../directives/image-url.directive";

interface RouteInfo {
  path: string;
  title: string;
  icon: string;
  class: string;
}
export const ROUTES: RouteInfo[] = [
  {
    path: "/dashboard",
    title: "Dashboard",
    icon: "dashboard",
    class: "",
  },
  {
    path: "/profile",
    title: "Profile",
    icon: "storefront",
    class: "",
  },
  {
    path: "/products",
    title: "Product List",
    icon: "inventory",
    class: "",
  },
  {
    path: "/orders",
    title: "Orders List",
    icon: "shopping_cart",
    class: "",
  },
  {
    path: "/messages",
    title: "Messages",
    icon: "forum",
    class: "",
  },
  {
    path: "/delivery_information",
    title: "Delivery Information",
    icon: "local_shipping",
    class: "",
  },
  {
    path: "/shop_guideline",
    title: "Merchant Guidelines",
    icon: "menu_book",
    class: "",
  },
  {
    path: "/user_guide",
    title: "User Guide",
    icon: "help_outline",
    class: "",
  },
];

@Component({
  selector: "fh-main-nav",
  templateUrl: "./main-nav.component.html",
  styleUrls: ["./main-nav.component.scss"],
  standalone: true,
  imports: [
    MatSidenavModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    AsyncPipe,
    RouterLink,
    RouterLinkActive,
    MatNavList,
    ImageUrlDirective,
  ],
})
export class MainNavComponent implements OnDestroy, OnInit {
  @ViewChild("profilePicInput") profilePicInput?: ElementRef<HTMLInputElement>;

  readonly defaultProfilePic = defaultProfilePic;

  username = "";
  profilePic = signal<string>("");
  isUploadingProfilePic = signal<boolean>(false);
  token = "";
  menuItems: any[];
  pageTitle = signal<string>("Fishyhub Merchant Portal");
  isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset).pipe(
    map((result) => result.matches),
    shareReplay()
  );
  tokenSubs!: Subscription;
  userSubs!: Subscription;
  routerSubs!: Subscription;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private auth: RequestService,
    private appStateService: AppStateService,
    private cdr: ChangeDetectorRef,
    private titleService: Title,
    public chatService: ChatService
  ) {
    this.menuItems = ROUTES;
  }

  ngOnInit() {
    this.tokenSubs = this.appStateService.userToken$.subscribe((val) => {
      this.token = val;
      // The Messages badge: polled only while signed in (and the tab is visible).
      if (val) this.chatService.startUnreadPolling();
      else this.chatService.stopUnreadPolling();
      this.cdr.detectChanges();
    });

    this.userSubs = this.appStateService.user$.subscribe((user) => {
      this.username = user.name;
      this.profilePic.set(user.profile_pic || "");
      this.cdr.detectChanges();
    });

    // Subscribe to router events to update page title
    this.routerSubs = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.updatePageTitle(event.urlAfterRedirects);
      });

    // Update title on initial load
    this.updatePageTitle(this.router.url);
  }

  private updatePageTitle(url: string): void {
    const routeTitle = this.getPageTitleFromUrl(url);
    const fullTitle = routeTitle
      ? `${routeTitle} - Fishyhub Merchant Portal`
      : "Fishyhub Merchant Portal";

    this.pageTitle.set(routeTitle || "Fishyhub Merchant Portal");
    this.titleService.setTitle(fullTitle);
    this.cdr.detectChanges();
  }

  private getPageTitleFromUrl(url: string): string | null {
    const cleanUrl = url.split("?")[0].split("#")[0];

    // Check exact matches first
    const exactMatch = ROUTES.find((route) => route.path === cleanUrl);
    if (exactMatch) {
      return exactMatch.title;
    }

    // Dynamic routes
    if (cleanUrl.startsWith("/product_input/")) {
      return "Product Details";
    }

    if (cleanUrl.startsWith("/order_details/")) {
      return "Order Details";
    }

    return null;
  }

  ngOnDestroy() {
    if (this.tokenSubs) {
      this.tokenSubs.unsubscribe();
    }
    if (this.userSubs) {
      this.userSubs.unsubscribe();
    }
    if (this.routerSubs) {
      this.routerSubs.unsubscribe();
    }
    this.chatService.stopUnreadPolling();
  }

  openProfilePicPicker(): void {
    if (this.isUploadingProfilePic()) return;
    this.profilePicInput?.nativeElement.click();
  }

  onProfilePicSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Allow re-selecting the same file later.
    input.value = "";
    if (!file) return;

    const form = new FormData();
    form.append("profile_pic", file);

    this.isUploadingProfilePic.set(true);
    this.auth
      .put<IApiResponse<{ profile_pic: string }>>(
        "/api/v1/merchant/profile/upload_profile_image",
        form
      )
      .subscribe({
        next: (res) => {
          this.isUploadingProfilePic.set(false);
          if (res.status === true) {
            // Update the sidebar avatar immediately — no reload needed.
            this.appStateService.updateProfilePic(res.data.profile_pic);
          }
        },
        error: () => {
          this.isUploadingProfilePic.set(false);
        },
      });
  }

  logout() {
    this.auth.delete("/api/v1/auth/logout").subscribe({
      next: () => this.finishLogout(),
      error: () => this.finishLogout(),
    });
  }

  // A dead httpOnly cookie can't be inspected/cleared from JS — hard-navigate so the app
  // fully re-bootstraps against /login, same as the interceptor's Unauthorized handling.
  private finishLogout(): void {
    localStorage.clear();
    this.appStateService.clearUserSession();
    window.location.href = "/login";
  }
}
